import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/Button';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePrompt } from '@/components/ui/PromptDialog';
import type { FinBankAccount, FinBankFeedConfig, FinBankTransaction, FinPayment } from '@/types/finance';

export function FinanceBankingPage() {
  const { prompt, dialog: promptDialog } = usePrompt();
  const paymentsPagination = usePagination(15);
  const [accounts, setAccounts] = useState<FinBankAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [unmatched, setUnmatched] = useState<FinBankTransaction[]>([]);
  const [payments, setPayments] = useState<FinPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [statementDate, setStatementDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [statementBalance, setStatementBalance] = useState('');
  const [feeds, setFeeds] = useState<FinBankFeedConfig[]>([]);
  const [feedType, setFeedType] = useState('OFX');
  const [feedName, setFeedName] = useState('');

  const loadPayments = useCallback(() => {
    setPaymentsLoading(true);
    financeApi
      .listPayments({ page: paymentsPagination.page, size: paymentsPagination.size })
      .then((r) => {
        setPayments(r.content);
        paymentsPagination.applyPageResponse(r);
      })
      .catch((e) => showApiError(e, 'Failed to load payments'))
      .finally(() => setPaymentsLoading(false));
  }, [paymentsPagination.page, paymentsPagination.size, paymentsPagination]);

  useEffect(() => {
    financeApi.listBankAccounts().then((list) => {
      setAccounts(list);
      if (list.length > 0) {
        setSelectedAccountId(list[0].id);
      }
    }).catch((e) => showApiError(e, 'Failed to load bank accounts'));
    financeApi.listBankFeeds().then(setFeeds).catch(() => setFeeds([]));
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    if (!selectedAccountId) return;
    financeApi.unmatchedTransactions(selectedAccountId).then(setUnmatched)
      .catch((e) => showApiError(e, 'Failed to load unmatched transactions'));
  }, [selectedAccountId]);

  async function onImportCsv(file: File | null) {
    if (!file || !selectedAccountId) return;
    setImporting(true);
    try {
      const result = await financeApi.importBankStatement(selectedAccountId, file);
      notify.success(`Imported ${result.imported} transactions`);
      setUnmatched(await financeApi.unmatchedTransactions(selectedAccountId));
    } catch (e) {
      showApiError(e, 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  async function onImportOfx(file: File | null) {
    if (!file || !selectedAccountId) return;
    setImporting(true);
    try {
      const result = await financeApi.importOfxStatement(selectedAccountId, file);
      notify.success(`Imported ${result.imported} OFX transactions`);
      setUnmatched(await financeApi.unmatchedTransactions(selectedAccountId));
    } catch (e) {
      showApiError(e, 'OFX import failed');
    } finally {
      setImporting(false);
    }
  }

  async function onImportMt940(file: File | null) {
    if (!file || !selectedAccountId) return;
    setImporting(true);
    try {
      const result = await financeApi.importMt940Statement(selectedAccountId, file);
      notify.success(`Imported ${result.imported} MT940 transactions`);
      setUnmatched(await financeApi.unmatchedTransactions(selectedAccountId));
    } catch (e) {
      showApiError(e, 'MT940 import failed');
    } finally {
      setImporting(false);
    }
  }

  async function onSaveFeed() {
    if (!selectedAccountId || !feedName.trim()) return;
    try {
      await financeApi.saveBankFeed({
        bankAccountId: selectedAccountId,
        feedType,
        feedName: feedName.trim(),
      });
      notify.success('Bank feed configured');
      setFeeds(await financeApi.listBankFeeds());
      setFeedName('');
    } catch (e) {
      showApiError(e, 'Failed to save feed config');
    }
  }

  async function onAutoMatch() {
    if (!selectedAccountId) return;
    try {
      const result = await financeApi.autoMatchTransactions(selectedAccountId);
      notify.success(`Auto-matched ${result.matched} transactions`);
      setUnmatched(await financeApi.unmatchedTransactions(selectedAccountId));
    } catch (e) {
      showApiError(e, 'Auto-match failed');
    }
  }

  async function onManualMatch(txId: number) {
    const raw = await prompt({ title: 'Match transaction', label: 'Payment ID to match' });
    if (!raw) return;
    const paymentId = Number(raw);
    if (!paymentId) return;
    try {
      await financeApi.manualMatchTransaction(txId, paymentId);
      if (selectedAccountId) {
        setUnmatched(await financeApi.unmatchedTransactions(selectedAccountId));
      }
    } catch (e) {
      showApiError(e, 'Manual match failed');
    }
  }

  async function onFinalizeReconciliation() {
    if (!selectedAccountId) return;
    const balance = Number(statementBalance);
    if (!statementDate || Number.isNaN(balance)) {
      notify.error('Enter a valid statement date and balance');
      return;
    }
    setFinalizing(true);
    try {
      const result = await financeApi.finalizeReconciliation(selectedAccountId, statementDate, balance);
      notify.success(`Reconciliation finalized (${result.statusCode}) for ${result.statementDate}`);
    } catch (e) {
      showApiError(e, 'Finalize reconciliation failed');
    } finally {
      setFinalizing(false);
    }
  }

  return (
    <div>
      {promptDialog}
      <PageHeader
        title="Banking & Reconciliation"
        description="Import statements, auto-match, and manually reconcile bank transactions"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Banking' }]}
      />
      <div className="card p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <Select
            className="min-w-[12rem]"
            placeholder="Select bank account"
            value={selectedAccountId != null ? String(selectedAccountId) : ''}
            onChange={(v) => setSelectedAccountId(v ? Number(v) : null)}
            options={accounts.map((a) => ({
              value: String(a.id),
              label: `${a.accountName} (${a.currencyCode})`,
            }))}
          />
          <label className="btn btn-secondary cursor-pointer">
            {importing ? 'Importing…' : 'Import CSV'}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onImportCsv(e.target.files?.[0] ?? null)}
            />
          </label>
          <label className="btn btn-secondary cursor-pointer">
            Import OFX
            <input
              type="file"
              accept=".ofx,.qfx,text/xml,application/x-ofx"
              className="hidden"
              onChange={(e) => onImportOfx(e.target.files?.[0] ?? null)}
            />
          </label>
          <label className="btn btn-secondary cursor-pointer">
            Import MT940
            <input
              type="file"
              accept=".940,.txt,text/plain"
              className="hidden"
              onChange={(e) => onImportMt940(e.target.files?.[0] ?? null)}
            />
          </label>
          <button type="button" className="btn btn-primary" onClick={onAutoMatch} disabled={!selectedAccountId}>
            Auto-match
          </button>
        </div>
        <div className="flex flex-wrap gap-3 items-end border-t pt-4">
          <DatePicker
            label="Statement date"
            value={statementDate}
            onChange={setStatementDate}
          />
          <Input
            type="number"
            step="0.01"
            label="Statement balance"
            placeholder="0.00"
            value={statementBalance}
            onChange={(e) => setStatementBalance(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={onFinalizeReconciliation}
            disabled={!selectedAccountId || finalizing || unmatched.length > 0}
            title={unmatched.length > 0 ? 'Match all transactions before finalizing' : undefined}
          >
            {finalizing ? 'Finalizing…' : 'Finalize reconciliation'}
          </button>
        </div>
        <p className="text-xs text-slate-500">CSV: date,description,amount[,reference] · OFX/MT940 bank feed formats supported</p>
        <div className="border-t pt-4 space-y-3">
          <h3 className="font-semibold">Bank feed configuration</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <Select
              label="Feed type"
              className="min-w-[8rem]"
              options={[
                { value: 'OFX', label: 'OFX' },
                { value: 'MT940', label: 'MT940' },
                { value: 'CSV', label: 'CSV' },
                { value: 'PLAID', label: 'Plaid (config only)' },
              ]}
              value={feedType}
              onChange={setFeedType}
            />
            <Input label="Feed name" value={feedName} onChange={(e) => setFeedName(e.target.value)} />
            <Button type="button" variant="secondary" onClick={() => void onSaveFeed()} disabled={!selectedAccountId}>
              Save feed
            </Button>
          </div>
          {feeds.length > 0 && (
            <ul className="text-xs text-slate-600 space-y-1">
              {feeds.map((f) => (
                <li key={f.id}>
                  {f.feedName} ({f.feedType}) — account #{f.bankAccountId}
                  {f.lastSyncAt ? ` · last sync ${f.lastSyncAt}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
        <h3 className="font-semibold">Unmatched bank transactions</h3>
        {unmatched.length === 0 ? (
          <p className="text-sm text-slate-500">No unmatched transactions.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Description</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {unmatched.map((tx) => (
                <tr key={tx.id} className="border-b border-slate-100">
                  <td className="p-2">{tx.transactionDate}</td>
                  <td className="p-2">{tx.description ?? tx.referenceNumber}</td>
                  <td className="p-2 text-right font-mono">{tx.amount}</td>
                  <td className="p-2 text-right">
                    <button type="button" className="text-primary-600 text-xs" onClick={() => onManualMatch(tx.id)}>
                      Match payment
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <h3 className="font-semibold mt-6">Recent payments (for matching)</h3>
        <ul className="text-sm space-y-1">
          {payments.map((p) => (
            <li key={p.id}>#{p.id} {p.paymentNumber} — {p.amount} on {p.paymentDate}</li>
          ))}
        </ul>
        <TablePagination
          page={paymentsPagination.page}
          totalPages={paymentsPagination.totalPages}
          totalElements={paymentsPagination.totalElements}
          onPageChange={paymentsPagination.setPage}
          loading={paymentsLoading}
          hasRows={payments.length > 0}
        />
      </div>
    </div>
  );
}
